"use client";
import React, { useState } from "react";
import { Box, Typography, TextField, Button, Container } from "@mui/material";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const SubscribeBlog = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");

  const validateEmail = (email) => {
    if (!email.trim()) return true;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setSubmitStatus("idle");

    if (value === "") {
      setError("");
    } else if (!validateEmail(value)) {
      setError(t("subscribeBlog.emailError"));
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (error || !email.trim()) return;

    try {
      setIsSubmitting(true);

      const response = await fetch("/api/save-blog-newsletter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      setEmail("");
      setError("");
      setSubmitStatus("success");

      setTimeout(() => {
        setSubmitStatus("idle");
      }, 5000);
    } catch (err) {
      console.error("Error storing email:", err);
      setSubmitStatus("error");
      setError(t("subscribeBlog.emailFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        py: 4,
        background: "#EFF0F3",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: { xs: "100px", sm: "120px" },
              height: { xs: "100px", sm: "120px" },
              mt: "2%",
              mb: "2%",
            }}
          >
            <Image
              src={IMAGES.SUBSCRIBE_BLOG_IMG}
              alt="Subscribe to weekly ai in recruiting automation insights newsletter"
              layout="responsive"
              width={80}
              height={80}
              priority
            />
          </Box>

          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "bold",
              color: "#2B2C34",
              fontFamily: "var(--font-plusjakarta)",
              fontSize: isArabic
                ? { xs: "18px", sm: "22px", md: "26px" }
                : { xs: "16px", sm: "20px", md: "26px" },
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {t("subscribeBlog.title")}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#828282",
              mb: 3,
              fontWeight: 400,
              fontSize: { xs: "16px", sm: "1.1rem" },
              mt: "1%",
              fontFamily: "var(--font-outfit)",
              textAlign: "center",
            }}
          >
            {t("subscribeBlog.subtitle")}
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", sm: "row" },
              width: "100%",
              maxWidth: "500px",
            }}
          >
            <TextField
              fullWidth
              placeholder={t("subscribeBlog.emailPlaceholder")}
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              disabled={isSubmitting}
              error={!!error}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": { borderColor: "#fff" },
                  "&:hover fieldset": { borderColor: "#fff" },
                  "&.Mui-focused fieldset": { borderColor: "#fff" },
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={!!error || !email.trim() || isSubmitting}
              sx={{
                backgroundColor: "#000",
                fontFamily: "var(--font-outfit)",
                borderRadius: "50px",
                whiteSpace: "nowrap",
                fontWeight: "500",
                fontSize: { xs: "16px", sm: "15px" },
                color: "#FFFFFE",
                px: 5,
                py: 1,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#775CE5",
                  boxShadow: "none",
                },
                "&:disabled": {
                  bgcolor: "#775CE5",
                  color: "#fff",
                },
              }}
            >
              {isSubmitting
                ? t("subscribeBlog.subscribing")
                : t("subscribeBlog.cta")}
            </Button>
          </Box>

          {/* Success/Error Messages */}
          <Box
            sx={{
              width: "100%",
              textAlign: isArabic ? "right" : "left",
              ml: isArabic ? 0 : "43%",
              mr: isArabic ? "43%" : 0,
              height: "20px",
            }}
          >
            {error && submitStatus !== "success" && (
              <Typography sx={{ color: "red", fontSize: "0.875rem" }}>
                {error}
              </Typography>
            )}
            {submitStatus === "success" && (
              <Typography sx={{ color: "#4CAF50", fontSize: "0.875rem" }}>
                {t("subscribeBlog.successMessage")}
              </Typography>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SubscribeBlog;
